import React from 'react'

// Components
import {
  Button,
  TimeAgo,
  Modal,
  useModalContext,
  ModalInjectedProps,
} from '@sketch/components'

// Local Components
import AccessScope, { allScopes } from '../../components/AccessScope'
import Divider from '../../components/Divider'
import DeleteTokenConfirmationModal from '../DeleteTokenConfirmationModal'
import TokenDetailsItem from '../../components/TokenDetailsItem'

// Styles
import {
  TokenDetailsRow,
  TokenDetailsModalBody,
  PermissionsHeading,
} from './TokenDetailsModal.styles'

// Utils
import { dateFormat } from '@sketch/utils'

// Types
import { PersonalTokenFragment } from '@sketch/gql-types'

const localizeDate = (dateStr: string): string => dateFormat(new Date(dateStr))

interface TokenDetailsModalProps extends ModalInjectedProps {
  token: PersonalTokenFragment
}

const TokenDetailsModal: React.FC<TokenDetailsModalProps> = props => {
  const { token, hideModal } = props
  const { showModal } = useModalContext()
  const handleConfirmDeleteClick = () => {
    hideModal()
    showModal(DeleteTokenConfirmationModal, { token })
  }

  const permissions = allScopes.map(scope => {
    const enabled = token.accessScopes.includes(scope)
    return <AccessScope key={scope} scope={scope} enabled={enabled} />
  })

  const expiresAt = token.expiresAt ? localizeDate(token.expiresAt) : 'Never'

  const lastUsedAt = token.lastUsedAt ? (
    <TimeAgo date={token.lastUsedAt} />
  ) : (
    'Unused'
  )

  return (
    <Modal title="Token Details" onCancel={hideModal}>
      <TokenDetailsModalBody>
        <TokenDetailsRow>
          <TokenDetailsItem>
            <span>DESCRIPTION</span>
            <span>{token.description}</span>
          </TokenDetailsItem>
          <TokenDetailsItem>
            <span>CREATED ON</span>
            <span>{localizeDate(token.createdAt)}</span>
          </TokenDetailsItem>
        </TokenDetailsRow>
        <TokenDetailsRow>
          <TokenDetailsItem>
            <span>EXPIRES ON</span>
            <span>{expiresAt}</span>
          </TokenDetailsItem>
          <TokenDetailsItem>
            <span>LAST USED</span>
            <span>{lastUsedAt}</span>
          </TokenDetailsItem>
        </TokenDetailsRow>

        <Divider />

        <PermissionsHeading>PERMISSIONS</PermissionsHeading>

        {permissions}
      </TokenDetailsModalBody>
      <Modal.Footer>
        <Button onClick={handleConfirmDeleteClick} variant="negative-secondary">
          Delete...
        </Button>
        <Button onClick={hideModal}>Close</Button>
      </Modal.Footer>
    </Modal>
  )
}

export default TokenDetailsModal
